/** @jsx isolateComponent */

import { FlexLayout, Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { Button, Colors, Heading, Margin, Text } from '@zapier/design-system';

import { useSize } from '@zapier/promotions/src/hooks';

type HardshipReliefModalProps = {
  /** Continue button will route to this url if provided. */
  continueButtonHref?: string;
  /**
   * The text that will appear on the continue button.
   * @default 'Continue to downgrade'
   **/
  continueButtonText?: string;
  /** True if the account has ever had the SBAP discount applied to it. */
  hasHadSbapDiscountBefore: boolean;
  /** The function called when the cancel or X buttons are clicked. */
  onClose: () => void;
  /** The function called when the continue button is clicked. */
  onContinue?: () => void;
};

const applyButtonColorThemeBase = {
  backgroundColor: Colors.white,
  color: Colors.grey900,
};

const applyButtonColorTheme = {
  custom: {
    base: applyButtonColorThemeBase,
    disabled: {},
    hover: applyButtonColorThemeBase,
    focus: {
      ...applyButtonColorThemeBase,
      outlineColor: Colors.white,
    },
  },
};

const continueButtonColorTheme = {
  custom: {
    base: {
      backgroundColor: '#FFD899',
      color: Colors.grey900,
    },
    disabled: {},
    hover: {
      backgroundColor: Colors.white,
      color: Colors.grey900,
    },
    focus: {
      backgroundColor: Colors.white,
      outlineColor: Colors.white,
      color: Colors.grey900,
    },
  },
};

type Content = {
  applyButtonText: string;
  applyButtonUrl: string;
  body: string;
  fullStoryEventName: string;
  title: string;
};

/**
 * A modal that directs users to the hardship relief form when trying to downgrade.
 */
export const HardshipReliefModal = ({
  continueButtonHref,
  continueButtonText = 'Continue to downgrade',
  hasHadSbapDiscountBefore,
  onClose,
  onContinue,
}: HardshipReliefModalProps) => {
  const size = useSize();

  const content: Content = hasHadSbapDiscountBefore
    ? {
        applyButtonText: 'Apply for extension',
        applyButtonUrl: 'https://zapier.wufoo.com/forms/m175frmk1r3p4z7/',
        body:
          'We care about keeping you as a customer and can offer a one-time extension on our Small Business Assistance program, so you can keep using Zapier at no charge for the next three months.',
        fullStoryEventName:
          'Viewed Hardship Relief Modal - Extending Applicant',
        title: 'Still need help?',
      }
    : {
        applyButtonText: 'Learn more',
        applyButtonUrl: 'https://zapier.com/blog/covid-19-assistance-program/',
        body:
          "We're offering free 3-month credits to help qualifying small business customers keep using Zapier at no charge.",
        fullStoryEventName: 'Viewed Hardship Relief Modal - New Applicant',
        title: 'Has your small business been affected by COVID-19?',
      };

  const renderContinueButton = () => {
    const buttonProps = {
      ariaLabel: continueButtonText,
      color: 'custom' as 'custom',
      colorTheme: continueButtonColorTheme,
      size: 'medium' as 'medium',
    };

    /**
     * If `href` and `onClick` are both set (even if one is set to undefined),
     * it messes up the Button component click behavior.
     */
    return continueButtonHref ? (
      <Button
        {...buttonProps}
        href={continueButtonHref}
        isFullWidth={size === 'small'}
      >
        {continueButtonText}
      </Button>
    ) : (
      <Button
        {...buttonProps}
        isFullWidth={size === 'small'}
        onClick={onContinue}
      >
        {continueButtonText}
      </Button>
    );
  };

  return (
    <Modal
      backgroundColor="linear-gradient(213.52deg, #FF4A00 0%, #FFC43E 101.17%)"
      closeButtonColor={Colors.white}
      closeOnOutsideClick={true}
      fullStoryEventName={content.fullStoryEventName}
      onClose={onClose}
    >
      <Heading>
        <Text color="white" fontWeight={600} type="subtitle900">
          {content.title}
        </Text>
      </Heading>

      <Margin margin="10px 0 55px">
        <Text color="white" fontWeight={600} tag="p" type="subtitle800">
          {content.body}
        </Text>
      </Margin>

      <FlexLayout direction={size === 'small' ? 'column' : 'row'}>
        <Button
          color="custom"
          colorTheme={applyButtonColorTheme}
          href={content.applyButtonUrl}
          isFullWidth={size === 'small'}
          size="medium"
        >
          {content.applyButtonText}
        </Button>

        {renderContinueButton()}
      </FlexLayout>
    </Modal>
  );
};
